<!-- 投稿一覧のサイドメニュー -->
<template>
  <div class="message-side-menu pt-4">
    <!-- 新規投稿作成ボタン -->
    <!-- MessageListで設定されているMessageEditModalを開く -->
    <div v-if="creatable" class="mb-3 px-4">
      <b-button
        class="create-btn"
        variant="outline-secondary my-auto"
        v-on:click="onMessageCreateClicked"
      >
        <b-icon-plus class="text-left mr-2" />
        <span class="mr-2">新規投稿作成</span>
      </b-button>
    </div>

    <!--検索フォーム-->
    <search-form class="px-4" ref="search" />

    <!-- 話題一覧に戻る -->
    <div class="back-to-topiclist mt-4 px-4" v-on:click="back"><b-icon-chevron-left />話題一覧</div>

    <!-- 話題一覧 -->
    <topic-list
      :domainId="domainId"
      :categories="categories"
      :topicList="topicList"
      :activeTopicId="topicId"
      :users="users"
      :me="me"
      :categoryFilter="categoryFilter"
      :fab="false"
      :in-sidemenu="true"
    />
  </div>
</template>

<style scoped>
.message-side-menu {
  display: flex;
  flex-direction: column;
  height: 100%;
}
</style>

<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  onBeforeMount,
  ref,
  type PropType,
} from "vue";
import type Vue from "vue";
import { useAppStore } from "@/store";
import SearchForm from "./SearchForm.vue";
import { Category, Topic, User } from "../model";
import TopicList from "./TopicList.vue";
import "./message-side-menu.scss";
import { allowCreateMessage, allowFeature, FeatureName } from "../direct-app-config";
import AclManager from "../model/acl-manager";
import { useLegacyRootEvent } from "@/composables/useLegacyRootEvent";

export default defineComponent({
  name: "topic-side-menu-panel",
  components: {
    TopicList,
    SearchForm,
  },
  props: {
    id: { type: String, default: () => "" },
    /** true: 複数選択モード */
    selectMulti: { type: Boolean, default: () => true },
    // カテゴリー一覧
    categories: { type: Array as PropType<Category[]>, default: () => [] },
    // フィルタ設定されているカテゴリー一覧
    filter_init: { type: Array as PropType<Category[]>, default: () => [] },
    // topicList用
    domainId: { type: String, default: () => "" },
    topicList: { type: Array as PropType<Topic[]>, default: () => [] },
    topicId: { type: String, default: () => "" }, // アクティブな話題Id
    users: { type: Array as PropType<User[]>, default: () => [] }, // 組織内ユーザー情報
    me: { type: Object as PropType<User>, default: () => User.createNotFoundUser() }, // ログインユーザ情報
    categoryFilter: { type: Array as PropType<Category[]>, default: () => [] }, // カテゴリフィルタ
  },
  setup(props) {
    const rootEvent = useLegacyRootEvent();
    // $bvModal / $router は BootstrapVue / vue-router が生やす（Step 3 で composable 化予定）
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    const filter = ref<Category[]>([]);

    // 新規作成ボタンが有効かどうか
    const creatable = computed((): boolean => {
      if (allowFeature("deny-edit") == false) return true; // 未対応組織
      return (
        AclManager.createMessage(AclManager.store, props.domainId) && // 契約観点
        AclManager.getCreateMessageAcl(AclManager.store, props.domainId, props.topicId) != "deny"
      ); // Acl観点
    });

    // Feature
    const hasFeature = (keyword: FeatureName): boolean => {
      return allowFeature(keyword);
    };

    const _onClick = (category: Category): void => {
      // 複数選択有り
      const index = filter.value.findIndex((item) => item.id == category.id);
      if (0 <= index) {
        filter.value.splice(index, 1);
      } else {
        filter.value.push(category);
      }
    };

    // 項目の選択状態
    // フィルタが空 → 「すべて」のみON
    // ファイルに何か設定 → 「すべて」OFF、該当項目がON
    const selected = (category: Category | "all"): { [selector: string]: boolean } => {
      if (filter.value.length === 0) {
        return { selected: category === "all" };
      } else if (category == "all") {
        return { selected: filter.value.length === 0 };
      } else {
        return { selected: 0 <= filter.value.findIndex((item) => item.id == category.id) };
      }
    };

    // カテゴリーの選択処理
    // 「すべて」が選択された場合、「すべて」のみになる → filter が空
    // その他の項目が選択された場合は、該当項目のON/OFF
    const onClick = (category: Category | "all"): void => {
      if (category === "all") {
        if (0 === filter.value.length) {
          // 何もしない
        } else {
          filter.value.splice(0, filter.value.length);
        }
        return;
      } else {
        _onClick(category);
      }
    };

    // 話題の新規作成ボタンを押した
    const onMessageCreateClicked = (): void => {
      if (allowCreateMessage(props.domainId, props.topicId) == false) {
        rootEvent.emit("free-alert");
        return;
      }
      vm?.$bvModal.show("modal-message");
    };

    const back = (): void => {
      if (vm?.$router) {
        useAppStore().setDomainId(props.domainId);
        useAppStore().setTopicId(""); // topicIdのリセット
        vm.$router.push({ path: `/${props.domainId}` });
      }
    };

    // created 相当（props の一回スナップショット。リアクティブ追跡は不要）
    onBeforeMount(() => {
      filter.value.push(...props.filter_init);
    });

    return {
      filter,
      creatable,
      hasFeature,
      selected,
      onClick,
      onMessageCreateClicked,
      back,
    };
  },
});
</script>
